/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { IApiError, TParams } from '@global-types/index';
import { TBodyApi } from 'shared/types';
import { TApiResponse } from '@features/catalogs/additionality-projects/list/types';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addAdditionalityProject = (
	data: TBodyApi
): Promise<{
	response: TApiResponse;
}> => {
	return apiClient.post('/additionalities', data);
};

export const useAddAdditionalityProject = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: addAdditionalityProject,

		onSuccess: async () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'additionalityProjects.notifications.additionalityProjectSuccessfullyAdded'
				),
				color: 'success',
			});

			queryClient.invalidateQueries({
				queryKey: [
					'databases-additionalities',
					{
						page: page,
						size: size,
						queryParams: queryParams,
						sortParams: sortParams,
					},
				],
			});
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
